"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { AppFeature } from "@/lib/app-features"

interface MaintenanceStatus {
    enabled: boolean
    message: string
    features: AppFeature[]
    endDate: string | null
}

interface MaintenanceContextValue {
    maintenanceStatus: MaintenanceStatus | null
    isLoading: boolean
    error: Error | null
}

const MaintenanceContext = createContext<MaintenanceContextValue | undefined>(undefined)

interface MaintenanceProviderProps {
    children: ReactNode
}

export function MaintenanceProvider({ children }: MaintenanceProviderProps) {
    const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        let isMounted = true

        async function fetchMaintenanceStatus() {
            try {
                const response = await fetch('/api/maintenance')
                if (!response.ok) {
                    throw new Error('Failed to fetch maintenance status')
                }
                const data = await response.json()

                if (isMounted) {
                    setMaintenanceStatus(data)
                    setError(null)
                }
            } catch (err) {
                if (isMounted) {
                    console.error('Failed to fetch maintenance status:', err)
                    setMaintenanceStatus({
                        enabled: false,
                        message: '',
                        features: [],
                        endDate: null
                    })
                    setError(err instanceof Error ? err : new Error('Unknown error'))
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false)
                }
            }
        }

        fetchMaintenanceStatus()

        return () => {
            isMounted = false
        }
    }, [])

    return (
        <MaintenanceContext.Provider value={{ maintenanceStatus, isLoading, error }}>
            {children}
        </MaintenanceContext.Provider>
    )
}

export function useMaintenanceContext() {
    const context = useContext(MaintenanceContext)
    if (context === undefined) {
        throw new Error('useMaintenanceContext must be used within a MaintenanceProvider')
    }
    return context
}
